import * as Sentry from "@sentry/react";

import {
  QueryStatement,
  QueryStatementStatus,
} from "pages/DevelopWorkspace/workspace.types";

/**
 * Defines valid state transitions for query statements.
 * Special cases:
 * 1. Success -> Pending: Allowed for explain functionality, where we need to re-run
 *    the same query with a different explain type (physical/logical)
 * 2. Error/Cancelled: Terminal states, no transitions allowed
 */
const validStatusTransitions: Record<
  QueryStatementStatus,
  QueryStatementStatus[]
> = {
  [QueryStatementStatus.success]: [
    QueryStatementStatus.pending, // Allow transition back to pending for explain functionality
  ],
  [QueryStatementStatus.error]: [], // Terminal state
  [QueryStatementStatus.cancelled]: [], // Terminal state
  [QueryStatementStatus.pending]: [
    QueryStatementStatus.running,
    QueryStatementStatus.suspended,
    QueryStatementStatus.error,
    QueryStatementStatus.cancelled,
    QueryStatementStatus.pending,
  ],
  [QueryStatementStatus.running]: [
    QueryStatementStatus.suspended,
    QueryStatementStatus.error,
    QueryStatementStatus.cancelled,
    QueryStatementStatus.success,
    QueryStatementStatus.unknown,
    QueryStatementStatus.running,
  ],
  [QueryStatementStatus.suspended]: [
    QueryStatementStatus.running,
    QueryStatementStatus.error,
    QueryStatementStatus.cancelled,
    QueryStatementStatus.success,
    QueryStatementStatus.unknown,
    QueryStatementStatus.suspended,
  ],
  [QueryStatementStatus.unknown]: [
    QueryStatementStatus.running,
    QueryStatementStatus.suspended,
    QueryStatementStatus.error,
    QueryStatementStatus.cancelled,
    QueryStatementStatus.success,
    QueryStatementStatus.unknown,
    QueryStatementStatus.restoring,
  ],
  [QueryStatementStatus.restoring]: [
    QueryStatementStatus.running,
    QueryStatementStatus.suspended,
    QueryStatementStatus.error,
    QueryStatementStatus.cancelled,
    QueryStatementStatus.success,
    QueryStatementStatus.unknown,
    QueryStatementStatus.restoring,
  ],
};

// For debugging purposes, we want to log the query statement
const filterQSSensitiveData = (queryStatement: {
  prevStatus?: QueryStatementStatus;
  status?: QueryStatementStatus;
  statistics?: any;
  explainType?: string;
  executionType?: string;
}) => {
  return {
    prevStatus: queryStatement.prevStatus,
    status: queryStatement.status,
    statistics: queryStatement.statistics,
    explainType: queryStatement.explainType,
    executionType: queryStatement.executionType,
  };
};

/**
 * Validates if a query statement status transition is allowed.
 * Special handling for:
 * 1. No status change: Always allowed
 * 2. Statistics updates: Only allowed for running statements
 * 3. Success -> Pending: Allowed for explain functionality
 *
 * @param prevQueryStatement - The current query statement
 * @param nextQueryStatement - The updated query statement
 * @returns true if the transition is valid, false otherwise
 */
const isQueryStatementChangesValid = (
  prevQueryStatement: QueryStatement,
  nextQueryStatement: Partial<QueryStatement>
) => {
  const prevStatus = prevQueryStatement.status;
  const nextStatus = nextQueryStatement.status;

  if (!nextStatus) {
    // progress update that comes without a status update
    if (
      nextQueryStatement.statistics &&
      prevStatus !== QueryStatementStatus.running
    ) {
      // disallow statistics updates for non-running statements
      return false;
    }

    // no status change
    return true;
  }

  const debuggingInfo = {
    prevQueryStatement: filterQSSensitiveData(prevQueryStatement),
    nextQueryStatement: filterQSSensitiveData(nextQueryStatement),
  };

  // This is a temporal solution to handle the case when the prev status is not set or invalid (for some reason)
  if (!prevStatus) {
    const err = new Error(
      `QS status is missing. prevStatus: ${prevStatus}, nextStatus: ${nextStatus}`
    );
    console.log(err);
    Sentry.captureException(err, {
      extra: debuggingInfo,
    });
    // If prevStatus is not set, we assume the transition is valid (temporary solution)
    return true;
  }

  if (!validStatusTransitions[prevStatus]) {
    const err = new Error(
      `Invalid QS previous status: ${prevStatus}. nextStatus: ${nextStatus}`
    );
    Sentry.captureException(err, {
      extra: debuggingInfo,
    });
    // If prevStatus is not valid, we assume the transition is valid (temporary solution)
    return true;
  }

  if (!validStatusTransitions[prevStatus].includes(nextStatus)) {
    const err = new Error(
      `Invalid status transition from ${prevStatus} to ${nextStatus}`
    );
    Sentry.captureException(err, {
      extra: debuggingInfo,
    });
    return false;
  }

  return true;
};

export { isQueryStatementChangesValid };
